import { createRouter, createWebHistory } from 'vue-router';
import store from '@/store';

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/pages/LoginPage.vue'),
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import('@/pages/Dashboard.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/',
    name: 'Accounts',
    component: () => import('@/pages/Sessions.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/proxies',
    name: 'Proxies',
    component: () => import('@/pages/Proxies.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/accounts/health',
    name: 'AccountHealth',
    component: () => import('@/pages/AccountHealth.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/channels',
    name: 'Channels',
    component: () => import('@/pages/Channels.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/media',
    name: 'MediaLibrary',
    component: () => import('@/pages/MediaLibrary.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/prompts',
    name: 'Prompts',
    component: () => import('@/pages/PromptsPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/image-generator',
    name: 'ImageGenerator',
    component: () => import('@/pages/ImageGenerator.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/settings',
    name: 'Settings',
    component: () => import('@/pages/Settings.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/campaigns',
    name: 'Campaigns',
    component: () => import('@/pages/Campaigns.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/campaigns/create',
    name: 'CampaignCreate',
    component: () => import('@/pages/CampaignForm.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/campaigns/:id',
    name: 'CampaignDetail',
    component: () => import('@/pages/CampaignDetail.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/campaigns/:id/edit',
    name: 'CampaignEdit',
    component: () => import('@/pages/CampaignForm.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/action-log',
    name: 'ActionLog',
    component: () => import('@/pages/ActionLog.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/autopost',
    name: 'Autopost',
    component: () => import('@/pages/Autopost.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/pipelines',
    name: 'Pipelines',
    component: () => import('@/pages/pipelines/PipelinesPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/pipelines/:id',
    name: 'PipelineEditor',
    component: () => import('@/pages/pipelines/PipelineEditor.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/repost',
    name: 'Repost',
    component: () => import('@/pages/RepostPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/digest',
    name: 'Digest',
    component: () => import('@/pages/DigestPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/warmup',
    name: 'Warmup',
    component: () => import('@/pages/Warmup.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/parser',
    name: 'Parser',
    component: () => import('@/pages/Parser.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/personas',
    name: 'Personas',
    component: () => import('@/pages/Personas.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/channel-search',
    redirect: { name: 'parser-channels' },
  },
  {
    path: '/parser/channels',
    name: 'parser-channels',
    component: () => import('@/pages/ChannelParserPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/parser/groups',
    name: 'parser-groups',
    component: () => import('@/pages/GroupParserPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/parser/history',
    name: 'parser-history',
    component: () => import('@/pages/ParserHistory.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/logs',
    name: 'SystemLogs',
    component: () => import('@/pages/SystemLogs.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/queues',
    name: 'QueueManager',
    component: () => import('@/pages/QueueManager.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/users',
    name: 'Users',
    component: () => import('@/pages/Users.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/mass-actions',
    name: 'MassActions',
    component: () => import('@/pages/MassActions.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/content-plans',
    name: 'ContentPlans',
    component: () => import('../pages/ContentPlans.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/content-plans/create',
    name: 'ContentPlanCreate',
    component: () => import('../pages/ContentPlanForm.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/content-plans/:id/edit',
    name: 'ContentPlanEdit',
    component: () => import('../pages/ContentPlanForm.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/sessions/:sessionId/channels',
    name: 'SessionChannels',
    component: () => import('@/pages/SessionChannels.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/sessions/:sessionId/channels/:channelId',
    name: 'SessionChannelDetail',
    component: () => import('@/pages/SessionChannelDetail.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/admin-channels',
    name: 'AdminChannels',
    component: () => import('@/pages/AdminChannels.vue'),
    meta: { requiresAuth: true },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach((to, from, next) => {
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!store.getters.isAuthenticated && to.path !== '/login') {
      next('/login');
      return;
    }
  }
  if (to.matched.some(record => record.meta.requiresAdmin) && !store.getters.isAdmin) {
    next('/');
    return;
  }
  next();
});

export default router;
